// src/pages/AcademyCommunitySection.jsx
import React, { useEffect, useRef, useState } from "react";
import "./AcademyCommunitySection.css";

/* Snap-strip with prev/next, adjustable height, captions, and a micro-hint */
function SnapStrip({ images, ariaLabel, size = "standard" }) {
  const scrollerRef = useRef(null);
  const [showHint, setShowHint] = useState(true);
  const isTouch = typeof window !== "undefined" && matchMedia("(pointer: coarse)").matches;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() => {
      el.scrollTo({ left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [images, size]);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 2200);
    return () => clearTimeout(t);
  }, []);

  const scrollByAmount = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".ac-strip-item");
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className={`ac-strip ac-strip--${size}`}>
      <button
        className="ac-nav ac-nav--prev"
        aria-label="Scroll previous"
        onClick={() => scrollByAmount(-1)}
      >
        ‹
      </button>

      <div
        className="ac-strip-scroll"
        ref={scrollerRef}
        aria-label={ariaLabel}
        role="group"
        onPointerDown={() => setShowHint(false)}
        onWheel={() => setShowHint(false)}
      >
        {images.map((item, i) => {
          const obj = typeof item === "string" ? { src: item } : item;
          return (
            <figure className="ac-strip-item" key={i}>
              <img className="ac-strip-img" src={obj.src} alt={obj.alt || ""} loading="lazy" decoding="async" />
              {obj.caption && <figcaption className="ac-cap">{obj.caption}</figcaption>}
            </figure>
          );
        })}
      </div>

      <button
        className="ac-nav ac-nav--next"
        aria-label="Scroll next"
        onClick={() => scrollByAmount(1)}
      >
        ›
      </button>

      {/* Edge fades */}
      <div className="ac-edge ac-edge--left" aria-hidden="true" />
      <div className="ac-edge ac-edge--right" aria-hidden="true" />

      {/* Micro-hint */}
      {showHint && (
        <div className="ac-hint" aria-hidden="true">
          {isTouch ? "Swipe to browse" : "Drag to scroll"}
        </div>
      )}
    </div>
  );
}

export default function AcademyCommunitySection() {
  const rootRef = useRef(null);

  // Scoped scroll-reveal
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll(".af-observe"));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const stripA = [
    "https://cosmetinkshop.s3.us-east-2.amazonaws.com/studentslong_2.JPG",
    "https://cosmetinkshop.s3.us-east-2.amazonaws.com/studentslong_4.JPG",
    "https://cosmetinkshop.s3.us-east-2.amazonaws.com/studentslong_3.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/studentb4.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/sept23_1.JPG",
    "https://cosmetinkshop.s3.us-east-2.amazonaws.com/studentslong_1.JPG",
  ];

  const stripB = [
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/2025acad8.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/ClassGroupA.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group2.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group1.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/ClassGroupB.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group3.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group4.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group5.jpg",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/group6.jpg",
  ];

  const stripC = [
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/2025acad6.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/2025acad7.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/2025acad4.JPG",
    "https://cosacademyassets.s3.us-east-2.amazonaws.com/2025acad5.JPG",
  ];

  return (
    <section className="ac-section ac-section--clean" aria-label="Community & Support" ref={rootRef}>
      {/* Block 1 */}
      <div className="ac-block">
        <div className="ac-media af-observe">
          <SnapStrip images={stripA} ariaLabel="Student work & classroom moments A" size="standard" />
        </div>
        <div className="ac-copy af-observe">
          <h3 className="ac-title">
            <span className="ac-title-grad">Apprenticeship Opportunity</span>
          </h3>
          <p className="ac-text">
            We are proud to offer an apprenticeship option for our graduates. After completing your certification program, you’ll have the option to work on additional models in our facility.
          </p>
          <p className="ac-text">
            With flexible times and self-directed scheduling, our students build on their skills and grow their portfolio under the guidance of our master trainers.
          </p>
        </div>
      </div>

      {/* Block 2 — bigger photos */}
      <div className="ac-block">
        <div className="ac-media af-observe">
          <SnapStrip images={stripB} ariaLabel="Student work & classroom moments B" size="jumbo" />
        </div>
        <div className="ac-copy af-observe">
          <h3 className="ac-title">
            <span className="ac-title-grad">Lifetime Support</span>
          </h3>
          <p className="ac-text">
            With over a decade in permanent makeup and 27,000+ successful procedures, our master trainers are here to stay—plus you’ll be connected to a thriving network of graduates.
          </p>
          <p className="ac-text">
            As you grow in the world of permanent cosmetics, we’re committed to you for the long haul—answering questions, giving feedback, and helping you continue to do great work.
          </p>
        </div>
      </div>

      {/* Block 3 — bigger photos */}
      <div className="ac-block">
        <div className="ac-media af-observe">
          <SnapStrip images={stripC} ariaLabel="Extra academy gallery" size="jumbo" />
        </div>
        <div className="ac-copy af-observe">
          <h3 className="ac-title">
            <span className="ac-title-grad">A Community Built for Growth</span>
          </h3>
          <p className="ac-text">
            From first practice strokes to polished portfolios, our space and support systems are designed to help you evolve—with real feedback, real models, and real momentum.
          </p>
        </div>
      </div>
    </section>
  );
}
